// Plan Inferno section — multi-engine spatial impact allocation results.
//
// Renders a card with sub-tabs (one per engine present in the row set).
// The set is data-driven: any engine that has a row in
// project_inferno_results gets a tab. The freshwater_eutroph stub tab
// is always rendered so it lights up automatically when Track B6 lands.
//
// Feature-flagged via REACT_APP_ENABLE_INFERNO — when unset/false, the
// section renders nothing so legacy pages aren't disturbed.

import React, { useEffect, useState } from "react";
import { useInfernoResult } from "../../hooks/useInfernoResult";
import { Spinner } from "../../components/Spinner";
import { InmapEngineTable } from "./InmapEngineTable";
import { EasiurEngineTable } from "./EasiurEngineTable";
import { UsetoxEngineTable } from "./UsetoxEngineTable";
import { LcimpactEngineTable } from "./LcimpactEngineTable";
import { FreshwaterEutrophEngineTable } from "./FreshwaterEutrophEngineTable";

interface PlanInfernoSectionProps {
  projectId: string;
  runId: string;
}

const ENGINE_LABELS: Record<string, string> = {
  inmap: "InMAP",
  easiur: "EASIUR",
  usetox: "USEtox",
  lcimpact: "LC-IMPACT",
  freshwater_eutroph: "Freshwater Eutroph.",
};

// freshwater_eutroph is always offered as a tab (Track B6 stub-friendly).
const ALWAYS_TABS = ["freshwater_eutroph"] as const;
// Stable tab order: present-first in this order, then anything else.
const ENGINE_ORDER = ["inmap", "easiur", "usetox", "freshwater_eutroph", "lcimpact"];

function isFlagOn(): boolean {
  return process.env.REACT_APP_ENABLE_INFERNO === "true";
}

function labelFor(engine: string): string {
  return ENGINE_LABELS[engine] ?? engine;
}

export function PlanInfernoSection({ projectId, runId }: PlanInfernoSectionProps) {
  const [activeEngine, setActiveEngine] = useState<string | null>(null);
  const enabled = isFlagOn();
  const { data, isLoading, isError } = useInfernoResult(
    enabled ? projectId : "",
    enabled ? runId : "",
  );

  const rowsByEngine = new Map<string, any>();
  for (const row of data ?? []) rowsByEngine.set(row.engine, row);

  // Collect engine names: present rows + always-on stub tabs.
  const engineSet = new Set<string>([
    ...Array.from(rowsByEngine.keys()),
    ...ALWAYS_TABS,
  ]);
  const engines = ENGINE_ORDER.filter((e) => engineSet.has(e)).concat(
    Array.from(engineSet).filter((e) => !ENGINE_ORDER.includes(e)),
  );

  useEffect(() => {
    if (!engines.length) return;
    if (!activeEngine || !engines.includes(activeEngine)) {
      setActiveEngine(engines[0]);
    }
  }, [engines, activeEngine]);

  if (!enabled) return null;

  const TabButton = ({ engine }: { engine: string }) => {
    const isActive = engine === activeEngine;
    const hasRow = rowsByEngine.has(engine);
    return (
      <button
        type="button"
        onClick={() => setActiveEngine(engine)}
        className={`px-4 py-2 rounded-t-md border-b-2 transition text-sm font-medium ${
          isActive
            ? "border-quantum-500 text-quantum-400"
            : "border-transparent text-text-muted hover:text-quantum-400 hover:border-quantum-500/50"
        }`}
      >
        {labelFor(engine)}
        {!hasRow && <span className="ml-1 text-xs text-text-muted">(pending)</span>}
      </button>
    );
  };

  const renderActive = () => {
    if (!activeEngine) return null;
    const row = rowsByEngine.get(activeEngine);
    const payload: Record<string, any> = (row?.payload as Record<string, any>) || {};

    if (row?.error) {
      return (
        <p className="text-sm text-text-muted">
          {labelFor(activeEngine)} engine reported an error for this run: {row.error}
        </p>
      );
    }

    switch (activeEngine) {
      case "inmap":
        return <InmapEngineTable payload={payload} />;
      case "easiur":
        return <EasiurEngineTable payload={payload} />;
      case "usetox":
        return <UsetoxEngineTable payload={payload} />;
      case "lcimpact":
        return <LcimpactEngineTable payload={payload} />;
      case "freshwater_eutroph":
        return <FreshwaterEutrophEngineTable payload={payload} />;
      default:
        return (
          <pre className="text-xs text-text-secondary bg-surface-800 rounded p-3 overflow-x-auto">
            {JSON.stringify(payload, null, 2)}
          </pre>
        );
    }
  };

  return (
    <section className="mt-8 bg-surface-700 p-6 rounded-lg shadow border border-border">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-bold text-quantum-400">
            Plan Inferno — Spatial Impact Allocation
          </h3>
          <p className="text-xs text-text-muted mt-1">
            Per-engine, spatially-resolved damages from displaced fossil generation.
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-text-muted py-6">
          <Spinner size="sm" className="text-quantum-500" />
          Loading spatial allocation results…
        </div>
      )}

      {!isLoading && isError && (
        <p className="text-sm text-text-muted py-6">
          Plan Inferno results unavailable for this run.
        </p>
      )}

      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <p className="text-sm text-text-muted py-6">
          This run was completed before Plan Inferno was enabled. Re-queue this project to
          see spatially resolved results.
        </p>
      )}

      {!isLoading && !isError && (data?.length ?? 0) > 0 && (
        <>
          <div className="border-b border-border mb-4 flex flex-wrap gap-x-2">
            {engines.map((e) => (
              <TabButton key={e} engine={e} />
            ))}
          </div>
          {renderActive()}
        </>
      )}
    </section>
  );
}
